import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IIconIdForm } from '@campaign-form/campaign-form.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { environment } from '@core/environments/environment';
import { IUserUpdate } from '@core/interfaces/user.interface';
import { AuthService } from '@core/services/auth.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EAiConnectorSource,
  EAiConnectorTab,
} from '@shared/components/ai-connector-dialog/ai-connector-dialog.interface';
import { AiConnectorService } from '@shared/components/ai-connector-dialog/ai-connector.service';
import { BaseComponent } from '@shared/components/base.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { ProfileStore } from './profile.store';

@Component({
  imports: [
    TranslateModule,
    IconBadgeComponent,
    MatButtonModule,
    MatFormField,
    MatLabel,
    MatSelectModule,
    MatInputModule,
    FileUploaderComponent,
    HasPermissionDirective,
    ReactiveFormsModule,
    MatErrorComponent,
    ContentContainerComponent,
    AsyncPipe,
    DotsSpinnerComponent,
  ],
  providers: [ProfileStore],
  selector: 'app-profile',
  templateUrl: './profile.component.html',
})
export class ProfileComponent extends BaseComponent implements OnInit {
  public readonly profileForm: FormGroup = new FormGroup({
    language: new FormControl<string | null>(null),
    name: new FormControl<string | null>(null, Validators.required),
  });
  public readonly imageForm: FormGroup<IIconIdForm> = new FormGroup({
    iconId: new FormControl<string | null>(null),
    imageFile: new FormControl<ICustomFile | null>(null, imageExtensionValidator()),
  });

  public readonly store = inject(ProfileStore);
  public readonly authService = inject(AuthService);
  public readonly aiConnectorService = inject(AiConnectorService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly updateTrigger = signal(0);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly eventTrackingService = inject(EventTrackingService);

  private readonly fileUploaderComponent = viewChild<FileUploaderComponent>('fileUploaderComponent');

  constructor() {
    super();
    effect(() => {
      if (!this.store.loading$() && this.store.user$()) {
        const user = this.store.user$();
        AuthService.currentDbUserProfile = {
          iconId: user?.iconId ?? null,
          language: user?.language ?? null,
          name: user?.name ?? null,
        };

        this.profileForm.patchValue({
          language: user?.language ?? null,
          name: user?.name ?? null,
        });
        this.imageForm.patchValue({
          iconId: user?.iconId ?? null,
        });
      }
    });
  }

  public ngOnInit(): void {
    this.setUser();
  }

  public setUser(): void {
    const userData = AuthService.currentDbUser;

    if (userData) {
      const userLanguage = userData.language || this.getSystemLanguage();

      this.profileForm.patchValue({
        language: userLanguage,
        name: userData.name,
      });

      this.imageForm.patchValue({
        iconId: userData.iconId,
        imageFile: ImageUtilitiesService.isValidFileId(userData.iconId)
          ? FileUploaderService.createCustomFile(userData.iconId!)
          : null,
      });
      this.translate.use(userLanguage);
    }
  }

  public openChangePasswordDialog(): void {
    this.dialog.open(ChangePasswordComponent, {
      autoFocus: false,
    });
  }

  public onLanguageChange(language: string): void {
    this.profileForm.get('language')?.setValue(language);
  }

  public saveUserProfile(): void {
    if (this.profileForm.invalid) {
      return;
    }

    const userData = AuthService.currentDbUser;
    const userUpdate: IUserUpdate = {
      ...this.profileForm.value,
      iconId: this.imageForm.get('iconId')?.value ?? null,
    };

    const userId = userData?.id;

    if (userId) {
      const nameChanged = this.profileForm.get('name')?.dirty ?? false;
      const languageChanged = this.profileForm.get('language')?.dirty ?? false;

      this.store.updateUser({ id: userId, userUpdate });

      if (nameChanged) {
        this.eventTrackingService.track(EEventType.userNameChanged);
      }

      if (languageChanged) {
        this.eventTrackingService.track(EEventType.languageChanged);
      }

      const selectedLanguage = this.profileForm.get('language')?.value;

      if (selectedLanguage) {
        this.translate.use(selectedLanguage);
      }

      this.cdr.detectChanges();
    }
  }

  public onImageSelected(event: { file: File; id: string }): void {
    const imageFileControl = this.imageForm.get('imageFile');
    const iconControl = this.imageForm.get('iconId');

    imageFileControl?.setValue(event.file);
    imageFileControl?.markAsDirty();
    imageFileControl?.updateValueAndValidity();
    iconControl?.setValue(event.id);

    const fileInput = this.fileUploaderComponent()?.fileInputRef();

    if (fileInput) {
      fileInput.nativeElement.value = '';
    }
  }

  public onImageRemoved(): void {
    const imageFileControl = this.imageForm.get('imageFile');
    const iconControl = this.imageForm.get('iconId');

    imageFileControl?.setValue(null);
    imageFileControl?.markAsDirty();
    imageFileControl?.updateValueAndValidity();
    iconControl?.setValue(null);

    const fileInput = this.fileUploaderComponent()?.fileInputRef();

    if (fileInput) {
      fileInput.nativeElement.value = '';
    }
  }

  private getSystemLanguage(): string {
    return ['en', 'de'].includes(this.translate.currentLang) ? this.translate.currentLang : 'en';
  }

  protected readonly providerNames: Record<string, string> = {
    'facebook.com': 'Facebook',
    'google.com': 'Google',
    'twitter.com': 'Twitter',
  };

  protected readonly EAiConnectorSource = EAiConnectorSource;
  protected readonly aiAssistantsDescriptionKey = environment.aiConnectorChatGptEnabled
    ? 'pages.profile.aiAssistants.description'
    : 'pages.profile.aiAssistants.descriptionWithoutChatGpt';

  protected readonly aiAssistantRows = [
    {
      icon: 'icons/ai-connector/claude.svg',
      iconSize: 24,
      isBrandIcon: true,
      labelKey: 'shared.aiConnector.tabs.claude',
      subtitleKey: null,
      tab: EAiConnectorTab.claude,
    },
    ...(environment.aiConnectorChatGptEnabled
      ? [
          {
            icon: 'icons/ai-connector/chatgpt.svg',
            // The official Blossom SVG includes OpenAI's prescribed clear space (~50% of the canvas).
            iconSize: 48,
            isBrandIcon: true,
            labelKey: 'shared.aiConnector.tabs.chatgpt',
            subtitleKey: null,
            tab: EAiConnectorTab.chatgpt,
          },
        ]
      : []),
    {
      icon: 'icons/plug.svg',
      iconSize: 24,
      isBrandIcon: false,
      labelKey: 'shared.aiConnector.tabs.other',
      subtitleKey: 'pages.profile.aiAssistants.otherAppsSubtitle',
      tab: EAiConnectorTab.other,
    },
  ];

  protected readonly linkedProviders = computed(() => {
    this.updateTrigger();

    return this.authService.getProviderData();
  });

  protected readonly getProviderEmail = (providerId: string): string | null | undefined => {
    return this.linkedProviders()?.find((provider) => provider.providerId === providerId)?.email;
  };

  protected async signOut(): Promise<void> {
    await this.authService.signOut();
  }

  protected isProviderLinked(providerId: string): boolean {
    return this.linkedProviders()?.some((provider) => provider.providerId === providerId) ?? false;
  }

  protected async linkProvider(provider: 'google' | 'facebook' | 'twitter'): Promise<void> {
    try {
      switch (provider) {
        case 'google':
          await this.authService.linkWithGoogle();
          break;
        case 'facebook':
          await this.authService.linkWithFacebook();
          break;
        case 'twitter':
          await this.authService.linkWithTwitter();
          break;
      }

      this.updateTrigger.update((v) => v + 1);
      this.snackbarService.openSuccess(this.translate.instant('pages.profile.linkedAccounts.success'));
      // provider isn't in the master event property list — reusing `source` to carry which
      // provider was linked, since it's the closest fit and no dedicated field exists.
      this.eventTrackingService.track(EEventType.accountLinked, { source: provider });
    } catch {
      this.snackbarService.openError(this.translate.instant('pages.profile.linkedAccounts.error'));
    }
  }

  protected async unlinkProvider(providerId: string): Promise<void> {
    try {
      await this.authService.unlinkProvider(providerId);
      this.updateTrigger.update((v) => v + 1);
      this.snackbarService.openSuccess(this.translate.instant('pages.profile.linkedAccounts.unlinkSuccess'));
    } catch {
      this.snackbarService.openError(this.translate.instant('pages.profile.linkedAccounts.unlinkError'));
    }
  }
}
